import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

const privacySections = [
  ["privacy-controller", "Who is responsible"],
  ["privacy-data", "Information we collect"],
  ["privacy-use", "How we use information"],
  ["privacy-sharing", "Sharing and transfers"],
  ["privacy-cookies", "Cookies and analytics"],
  ["privacy-retention", "Retention and security"],
  ["privacy-rights", "Your choices and rights"],
  ["privacy-updates", "Updates and contact"],
];

function Privacy() {
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="policy-page privacy">
      <header className="policy-page__hero">
        <div>
          <p className="policy-page__kicker">Legal / Privacy</p>
          <h1>Privacy notice</h1>
        </div>
        <div className="policy-page__intro">
          <p>
            This notice explains how {siteConfig.legalName}, trading as {siteConfig.brandName}, handles personal
            information when you browse, shop or contact us.
          </p>
          <span>Last updated / September 2026</span>
        </div>
      </header>

      <div className="policy-page__layout">
        <aside className="policy-page__aside">
          <p className="policy-page__kicker">On this page</p>
          <nav aria-label="Privacy sections">
            {privacySections.map(([id, label]) => (
              <a href={`#${id}`} key={id}>{label}</a>
            ))}
          </nav>
        </aside>

        <article className="policy-page__content">
          <section id="privacy-controller">
            <p className="policy-page__section-number">01</p>
            <h2>Who is responsible</h2>
            <p>
              {siteConfig.legalName}, trading as {siteConfig.brandName}, is responsible for the personal information
              described in this notice. You can reach our privacy contact at
              {" "}<a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>{" "}or by writing to {siteConfig.contact.address}.
            </p>
            <p>
              This notice applies to this online store, client-care enquiries and
              related order services. A third-party service may provide its own
              notice where it decides independently how information is used.
            </p>
          </section>

          <section id="privacy-data">
            <p className="policy-page__section-number">02</p>
            <h2>Information we collect</h2>
            <p>Depending on how you interact with us, we may collect:</p>
            <ul>
              <li>identity and contact details, including name, email, telephone number and delivery address;</li>
              <li>order information, such as products, size, payment status, delivery and return history;</li>
              <li>messages, product reviews, preferences and client-care correspondence;</li>
              <li>technical information including IP address, device, browser, language and approximate region;</li>
              <li>usage events such as pages viewed, searches, basket actions and referral source; and</li>
              <li>marketing choices and records of consent or opt-out requests.</li>
            </ul>
            <p>
              Payment card details are entered into the payment provider’s secure
              environment. We generally receive confirmation and limited payment
              metadata rather than a complete card number.
            </p>
          </section>

          <section id="privacy-use">
            <p className="policy-page__section-number">03</p>
            <h2>How we use information</h2>
            <p>We use personal information only where a valid basis applies, including to:</p>
            <ul>
              <li>process payment, fulfil an order and provide delivery or return support;</li>
              <li>respond to questions and keep service records;</li>
              <li>prevent fraud, protect accounts and maintain site security;</li>
              <li>meet accounting, tax, consumer-protection and other legal duties;</li>
              <li>understand store performance and improve navigation, fit information and products; and</li>
              <li>send optional marketing where you have requested it or where law otherwise permits.</li>
            </ul>
            <p>
              The relevant basis may be performance of a contract, compliance
              with law, your consent, or our legitimate interests in operating a
              secure and useful store. You may withdraw consent at any time; this
              does not affect earlier lawful processing.
            </p>
          </section>

          <section id="privacy-sharing">
            <p className="policy-page__section-number">04</p>
            <h2>Sharing and international transfers</h2>
            <p>
              We may share only the information needed with hosting, payment,
              fraud-prevention, logistics, customer-service, analytics and
              professional advisers. These providers must protect information and
              may use it only for agreed services, except where they act under
              their own legal responsibilities.
            </p>
            <p>
              We may also disclose information where required by law, to protect
              rights or safety, or as part of a genuine corporate transaction. We
              do not sell personal information for money.
            </p>
            <p>
              If information is processed outside your country, we use an
              applicable transfer safeguard, such as an adequacy decision,
              approved contractual terms or another lawful mechanism.
            </p>
          </section>

          <section id="privacy-cookies">
            <p className="policy-page__section-number">05</p>
            <h2>Cookies and analytics</h2>
            <p>
              Essential cookies keep the basket, checkout, language and security
              features working. With permission where required, optional cookies
              may measure visits, remember preferences or help us understand
              campaign performance.
            </p>
            <p>
              Browser controls can block or delete cookies, although essential
              store functions may then be unavailable. Where a cookie preference
              tool appears, you can revisit it to change optional choices.
            </p>
          </section>

          <section id="privacy-retention">
            <p className="policy-page__section-number">06</p>
            <h2>Retention and security</h2>
            <p>
              We keep information only for as long as needed for the purpose
              collected, including warranty, dispute, tax and accounting periods.
              Marketing records are retained until you opt out or they are no
              longer useful; security records are retained for a proportionate
              period. Information is then deleted or anonymised where practical.
            </p>
            <p>
              Administrative, technical and organisational safeguards are used to
              reduce unauthorised access, loss or misuse. No internet service is
              completely risk-free, so please avoid sending payment credentials
              or other unnecessary sensitive information through contact forms.
            </p>
          </section>

          <section id="privacy-rights">
            <p className="policy-page__section-number">07</p>
            <h2>Your choices and rights</h2>
            <p>
              Depending on your location, you may ask to access, correct, delete,
              restrict or receive a portable copy of personal information. You
              may also object to certain processing, withdraw consent and opt out
              of direct marketing at any time.
            </p>
            <p>
              Email <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a> with enough detail to identify your request. We may need to
              verify identity before responding. You may also complain to the
              data-protection authority in your country. Our store is not directed
              to children, and we do not knowingly collect children’s information.
            </p>
          </section>

          <section id="privacy-updates">
            <p className="policy-page__section-number">08</p>
            <h2>Updates and contact</h2>
            <p>
              We may update this notice when services or legal requirements
              change. The current version and update date will remain on this
              page. Material changes will receive additional notice where needed.
            </p>
            <p>
              For privacy questions, use our <Link to="/contact" onClick={toTop}>
                contact page
              </Link> or email the address above.
            </p>
          </section>

          <nav className="policy-page__related" aria-label="Related policies">
            <Link to="/terms" onClick={toTop}>Terms and conditions</Link>
            <Link to="/shipping" onClick={toTop}>Shipping</Link>
            <Link to="/returns" onClick={toTop}>Returns</Link>
            <Link to="/cookies" onClick={toTop}>Cookie policy</Link>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
